/**
 * 批量任务相关类型定义
 * 用于批量图片生成任务的提交和状态追踪
 */

import type { GenerationSourceType } from './workflow'

// ==================== 批量任务状态枚举 ====================

/**
 * 批量任务状态
 */
export const BATCH_TASK_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  PARTIAL_FAILED: 'partial_failed',
  FAILED: 'failed',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
} as const

export type BatchTaskStatus =
  (typeof BATCH_TASK_STATUS)[keyof typeof BATCH_TASK_STATUS]

/**
 * 批量任务执行模式
 */
export const BATCH_TASK_MODE = {
  /** 即时执行 */
  INSTANT: 'instant',
  /** 计划执行（定时执行） */
  SCHEDULED: 'scheduled',
} as const

export type BatchTaskMode = (typeof BATCH_TASK_MODE)[keyof typeof BATCH_TASK_MODE]

/**
 * 批量任务执行模式中文映射
 */
export const BATCH_TASK_MODE_MAP: Record<
  BatchTaskMode,
  { text: string; color: string; bgColor: string }
> = {
  [BATCH_TASK_MODE.INSTANT]: {
    text: '即时执行',
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
  },
  [BATCH_TASK_MODE.SCHEDULED]: {
    text: '定时执行',
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-500/20',
  },
}

/**
 * 批量任务状态中文映射
 */
export const BATCH_TASK_STATUS_MAP: Record<
  BatchTaskStatus,
  { text: string; color: string }
> = {
  [BATCH_TASK_STATUS.PENDING]: { text: '等待中', color: 'text-muted-foreground' },
  [BATCH_TASK_STATUS.RUNNING]: { text: '处理中', color: 'text-blue-500' },
  [BATCH_TASK_STATUS.COMPLETED]: { text: '已完成', color: 'text-emerald-500' },
  [BATCH_TASK_STATUS.PARTIAL_FAILED]: {
    text: '部分失败',
    color: 'text-amber-500',
  },
  [BATCH_TASK_STATUS.FAILED]: { text: '失败', color: 'text-destructive' },
  [BATCH_TASK_STATUS.PAUSED]: { text: '已暂停', color: 'text-amber-500' },
  [BATCH_TASK_STATUS.CANCELLED]: { text: '已取消', color: 'text-muted-foreground' },
}

// ==================== 批量任务接口定义 ====================

/**
 * 批量任务提交参数
 */
export interface BatchTaskSubmitParams {
  /** 工作流 ID */
  workflow_id: string
  /** 图片 URL 列表（图生图工作流必填，文生图可为空） */
  image_urls: string[]
  /** 表单数据 */
  form_data: Record<string, unknown>
  /** 每个源单元的重复生成次数 */
  repeat_count?: number
  /** 计划执行时间（可选，ISO 8601 格式，最远7天内） */
  scheduled_at?: string
  /** 专享用户无可用设备时是否走共享池 */
  useSharedIfNoExclusive?: boolean
}

export type BatchTaskRoutingStatus =
  | 'routed_shared'
  | 'routed_exclusive'
  | 'waiting_for_capacity'

/**
 * 批量任务提交响应
 */
export interface BatchTaskSubmitResponse {
  /** 批量任务 ID */
  id: string
  /** 任务状态 */
  status: BatchTaskStatus
  /** 任务数量 */
  total_count: number
  /** 预计输出图片数量 */
  expected_output_count?: number
  /** 已完成输出图片数量 */
  completed_output_count?: number
  /** 生成源类型 */
  generation_source_type: GenerationSourceType
  /** 重复生成次数 */
  repeat_count: number
  /** 源单元数量 */
  source_count: number
  /** 预估算力消耗 */
  estimated_power: number
  /** 路由状态（专享/共享/等待容量） */
  routing_status?: BatchTaskRoutingStatus
  /** 路由原因 */
  routing_reason?: string
  /** 预计就绪时间（等待容量时可能返回） */
  estimated_ready_at?: string
  /** 创建时间 */
  created_at: string
}

/**
 * 单个任务结果
 */
export interface TaskResult {
  /** 任务索引 */
  index: number
  /** 输入图片 URL */
  input_url: string
  /** 任务状态 */
  status: string
  /** 输出 URL（成功时） */
  output_url?: string
  /** 输出 URL 列表（多图工作流成功时） */
  output_urls?: string[]
  /** 实际输出图片数量 */
  output_count?: number
  /** 预计输出图片数量 */
  expected_output_count?: number
  /** 错误信息（失败时） */
  error?: string
  /** 任务 ID */
  task_id: string
  /** 处理耗时（毫秒） */
  process_time_ms?: number
}

/**
 * 批量任务状态响应
 */
export interface BatchTaskStatusResponse {
  /** 批量任务 ID */
  id: string
  /** 工作流 ID */
  workflow_id: string
  /** 整体状态 */
  status: BatchTaskStatus
  /** 总任务数 */
  total_count: number
  /** 预计输出图片数量 */
  expected_output_count?: number
  /** 已完成输出图片数量 */
  completed_output_count?: number
  /** 生成源类型 */
  generation_source_type: GenerationSourceType
  /** 每个源单元的重复生成次数 */
  repeat_count: number
  /** 源单元数量 */
  source_count: number
  /** 排队中数量 */
  queued_count: number
  /** 运行中数量 */
  running_count: number
  /** 已完成数 */
  completed_count: number
  /** 失败数 */
  failed_count: number
  /** 已取消数 */
  cancelled_count?: number
  /** 已暂停数 */
  paused_count?: number
  /** 预估算力消耗 */
  estimated_power: number
  /** 已消耗算力 */
  consumed_power: number
  /** 各任务结果 */
  results: TaskResult[]
  /** 创建时间 */
  created_at: string
  /** 更新时间 */
  updated_at: string
  /** 完成时间 */
  completed_at?: string
}

/**
 * 批量任务实体
 */
export interface BatchTask {
  id: string
  workflow_id: string
  generation_source_type: GenerationSourceType
  user_id: string
  company_id: string
  status: BatchTaskStatus
  total_count: number
  expected_output_count?: number
  completed_output_count?: number
  repeat_count: number
  source_count: number
  completed_count: number
  failed_count: number
  paused_count?: number
  form_data: Record<string, unknown>
  image_urls: string[]
  estimated_power: number
  consumed_power: number
  task_ids: string[]
  created_at: string
  updated_at: string
  completed_at?: string
}

/**
 * 批量任务列表项（用于历史记录展示）
 */
export interface BatchTaskListItem {
  /** 批量任务 ID */
  id: string
  /** 工作流 ID */
  workflow_id: string
  /** 工作流名称 */
  workflow_name: string
  /** 任务状态 */
  status: BatchTaskStatus
  /** 任务执行模式 */
  task_mode: BatchTaskMode
  /** 计划执行时间（仅定时任务有值） */
  scheduled_at?: string
  /** 总任务数 */
  total_count: number
  /** 预计输出图片数量 */
  expected_output_count?: number
  /** 生成源类型 */
  generation_source_type: GenerationSourceType
  /** 每个源单元的重复生成次数 */
  repeat_count: number
  /** 源单元数量 */
  source_count: number
  /** 排队中数量 */
  queued_count: number
  /** 运行中数量 */
  running_count: number
  /** 已完成数量 */
  completed_count: number
  /** 已完成输出图片数量 */
  completed_output_count?: number
  /** 失败数量 */
  failed_count: number
  /** 已暂停数量 */
  paused_count?: number
  /** 已取消数量 */
  cancelled_count?: number
  /** 用户备注 */
  remark: string
  /** 已消耗算力 */
  consumed_power: number
  /** 创建时间 */
  created_at: string
  /** 是否启用多组提示词 */
  enable_multi_prompt?: boolean

  // ========== 操作者信息（主账号查看时返回）==========

  /** 操作者用户 ID（执行生图操作的用户 ID） */
  operator_id?: string
  /** 操作者用户名 */
  operator_username?: string
  /** 操作者头像 URL */
  operator_avatar?: string | null
  /** 是否为子账号操作（true 表示子账号执行，false 表示主账号执行） */
  is_sub_account?: boolean
}

/**
 * 批量任务列表查询参数
 */
export interface BatchTaskListParams {
  /** 页码 */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 备注搜索关键词（模糊匹配） */
  remark?: string
  /** 工作流 ID（可选，用于筛选特定工作流） */
  workflow_id?: string
}

// ==================== 批量任务详情（子任务结果） ====================

/**
 * 单个子任务结果状态
 * 注意：状态值与后端 entity/task.go 中的 TaskStatus 保持一致
 */
export const TASK_RESULT_STATUS = {
  /** 待处理（后端为 pending） */
  QUEUED: 'pending',
  /** 执行中 */
  RUNNING: 'running',
  /** 成功（后端为 success） */
  COMPLETED: 'success',
  /** 失败 */
  FAILED: 'failed',
  /** 已取消（任务被取消，未执行生图） */
  CANCELLED: 'cancelled',
} as const

export type TaskResultStatus =
  (typeof TASK_RESULT_STATUS)[keyof typeof TASK_RESULT_STATUS]

/**
 * 单个子任务结果项
 */
export interface BatchTaskResultItem {
  /** 子任务 ID */
  task_id: string
  /** 任务索引（从 0 开始） */
  index: number
  /** 任务状态 */
  status: TaskResultStatus
  /** 原始图片 URL */
  input_url: string
  /** 生成图片 URL（成功时） */
  output_url: string | null
  /** 生成图片 URL 列表（多图工作流成功时） */
  output_urls?: string[]
  /** 实际输出图片数量 */
  output_count?: number
  /** 预计输出图片数量 */
  expected_output_count?: number
  /** 错误信息（失败时） */
  error: string | null
  /** 处理耗时（毫秒） */
  process_time_ms: number | null
  /** 图片索引（0 到 M-1，标识是哪张原图） */
  image_index?: number
  /** 提示词索引（0 到 N-1，标识是哪个提示词） */
  prompt_index?: number
  /** 重复生成索引 */
  repeat_index?: number
  /** 分组索引 */
  group_index?: number
  /** 组内输出顺序 */
  group_output_index?: number
  /** 使用的提示词 */
  prompt?: string
  /** 原始文件名 */
  original_filename?: string
}

/**
 * 分组结果中的单个输出项
 */
export interface BatchTaskGroupedResultOutput {
  task_id: string
  status: TaskResultStatus
  output_url: string | null
  output_count?: number
  error: string | null
  process_time_ms: number | null
  prompt_index: number
  prompt?: string
  repeat_index: number
  group_output_index: number
  server_id?: string
  server_name?: string
  started_at?: string
  completed_at?: string
}

/**
 * 分组结果中的单个组
 */
export interface BatchTaskGroupedResultGroup {
  group_index: number
  generation_source_type: GenerationSourceType
  source_label?: string
  input_url?: string
  prompt?: string
  original_filename?: string
  total_outputs: number
  completed_outputs: number
  outputs: BatchTaskGroupedResultOutput[]
}

/**
 * 分组结果响应
 */
export interface BatchTaskGroupedResultsResponse {
  items: BatchTaskGroupedResultGroup[]
  total: number
}

/**
 * 批量任务详情信息
 */
export interface BatchTaskInfo {
  /** 批量任务 ID */
  id: string
  /** 工作流 ID */
  workflow_id: string
  /** 工作流名称 */
  workflow_name: string
  /** 生成源类型 */
  generation_source_type: GenerationSourceType
  /** 任务状态 */
  status: BatchTaskStatus
  /** 任务执行模式 */
  task_mode: BatchTaskMode
  /** 计划执行时间（仅定时任务有值） */
  scheduled_at?: string
  /** 总任务数 */
  total_count: number
  /** 预计输出图片数量 */
  expected_output_count?: number
  /** 排队中数量（可选，后端可能暂不返回） */
  queued_count?: number
  /** 运行中数量（可选，后端可能暂不返回） */
  running_count?: number
  /** 已完成数量 */
  completed_count: number
  /** 已完成输出图片数量 */
  completed_output_count?: number
  /** 失败数量 */
  failed_count: number
  /** 已暂停数量 */
  paused_count?: number
  /** 已取消数量 */
  cancelled_count?: number
  /** 用户备注 */
  remark: string
  /** 已消耗算力 */
  consumed_power: number
  /** 创建时间 */
  created_at: string
  /** 原始图片数量 */
  image_count?: number
  /** 提示词数量 */
  prompt_count?: number
  /** 重复生成次数 */
  repeat_count?: number
  /** 源单元数量 */
  source_count?: number
}

/**
 * 批量任务结果分页数据
 */
export interface BatchTaskResultsPagination {
  /** 子任务结果列表 */
  items: BatchTaskResultItem[]
  /** 总数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  page_size: number
}

/**
 * 批量任务详情响应（含子任务结果）
 */
export interface BatchTaskResultsResponse {
  /** 批次信息 */
  batch_info: BatchTaskInfo
  /** 子任务结果分页 */
  results: BatchTaskResultsPagination
  /** 分组结果 */
  grouped_results: BatchTaskGroupedResultsResponse
}

// ==================== 重试失败任务 ====================

/**
 * 重试失败任务请求参数
 */
export interface RetryTasksParams {
  /** 需要重试的子任务 ID 列表 */
  task_ids: string[]
}

/**
 * 重试失败任务响应
 */
export interface RetryTasksResponse {
  /** 成功重试的任务数量 */
  retried_count: number
  /** 预估消耗算力 */
  estimated_power: number
}

// ==================== 批量下载 ====================

/**
 * 下载类型
 */
export type DownloadType = 'all' | 'completed' | 'selected'

/**
 * 批量下载请求参数
 */
export interface DownloadBatchParams {
  /** 下载类型 */
  type: DownloadType
  /** 选中的任务 ID 列表（当 type 为 selected 时必填） */
  task_ids?: string[]
}

/**
 * 批量下载响应
 */
export interface DownloadBatchResponse {
  /** 下载链接 */
  download_url: string
  /** 链接过期时间 */
  expires_at: string
  /** 文件数量 */
  file_count: number
}

/**
 * 批量任务列表响应
 */
export interface BatchTaskListResponse {
  items: BatchTaskListItem[]
  total: number
  page: number
  page_size: number
}

// ==================== 上传进度类型 ====================

/**
 * 上传进度状态
 */
export interface UploadProgress {
  /** 总文件数 */
  total: number
  /** 已上传数 */
  uploaded: number
  /** 失败数 */
  failed: number
  /** 当前上传文件名 */
  currentFile: string
  /** 进度百分比 0-100 */
  percent: number
}

/**
 * 单个文件上传状态
 */
export type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'error'

/**
 * 上传文件项
 */
export interface UploadFileItem {
  /** 文件 ID */
  id: string
  /** 原始文件 */
  file: File
  /** 上传状态 */
  status: FileUploadStatus
  /** 上传后的 URL */
  url?: string | undefined
  /** 错误信息 */
  error?: string | undefined
  /** 预览 URL */
  previewUrl: string
}

// ==================== API 响应包装 ====================

/**
 * 任务 API 响应包装结构
 */
export interface TaskApiResponse<T> {
  code: number
  message: string
  data: T
  trace_id?: string
}
